import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart2, TrendingUp, FileText, ExternalLink, ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { fetchPressStatsByYear, fetchPressStatsByMonth, fetchPressStatsByPress } from '../api';

const TAB_YEAR = 'year';
const TAB_MONTH = 'month';
const TAB_PRESS = 'press';

function rankSources(srcMap) {
  return Object.entries(srcMap)
    .sort((a, b) => b[1] - a[1]);
}

function SourceBar({ source, count, max, rank, selected, onClick }) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  const colors = ['#38bdf8', '#34d399', '#fb923c', '#a78bfa', '#f472b6', '#facc15', '#4ade80'];
  const color = colors[rank % colors.length];
  return (
    <div onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem', cursor: onClick ? 'pointer' : 'default',
        background: selected ? 'rgba(56,189,248,0.07)' : 'transparent',
        border: selected ? '1px solid rgba(56,189,248,0.3)' : '1px solid transparent',
        borderRadius: '5px', padding: '0.1rem 0.3rem', marginLeft: '-0.3rem' }}>
      <div style={{ width: '90px', fontSize: '0.75rem', color: selected ? '#38bdf8' : '#94a3b8', textAlign: 'right', flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: selected ? 700 : 400 }} title={source}>
        {source}
      </div>
      <div style={{ flex: 1, background: '#1e293b', borderRadius: '3px', height: '16px', position: 'relative', minWidth: 0 }}>
        <div style={{ width: `${pct}%`, background: selected ? '#38bdf8' : color, height: '100%', borderRadius: '3px', transition: 'width 0.3s', minWidth: count > 0 ? '2px' : 0 }} />
      </div>
      <div style={{ width: '28px', fontSize: '0.75rem', color: selected ? '#38bdf8' : '#e2e8f0', fontWeight: 600, textAlign: 'right', flexShrink: 0 }}>
        {count}
      </div>
    </div>
  );
}

function SourcePressDrawer({ source, pressRows, onClose }) {
  const filtered = useMemo(() => {
    if (!pressRows || !source) return [];
    return pressRows
      .map(row => ({
        ...row,
        sources: row.sources.filter(s => s.source === source),
      }))
      .filter(row => row.sources.length > 0)
      .sort((a, b) => b.published_at.localeCompare(a.published_at));
  }, [pressRows, source]);

  if (!source) return null;
  return (
    <div style={{ marginTop: '1rem', background: '#0a1628', border: '1px solid rgba(56,189,248,0.25)', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', borderBottom: '1px solid #1e293b', background: '#0f172a' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#38bdf8' }}>
          {source} <span style={{ color: '#64748b', fontWeight: 400 }}>— 관련 보도자료 {filtered.length}건</span>
        </span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', display: 'flex', alignItems: 'center' }}><X size={13} /></button>
      </div>
      <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
        {filtered.map((row, ri) => (
          <div key={row.id} style={{ padding: '0.5rem 0.75rem', borderBottom: ri < filtered.length - 1 ? '1px solid #0f172a' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <span style={{ fontSize: '0.7rem', color: '#475569', flexShrink: 0 }}>{row.published_at}</span>
              <span style={{ fontSize: '0.82rem', color: '#cbd5e1', fontWeight: 600, lineHeight: 1.4 }}>{row.title}</span>
            </div>
            {row.sources.map((s, si) => (
              <div key={si} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingLeft: '0.5rem', marginTop: '0.2rem' }}>
                <span style={{ fontSize: '0.65rem', color: '#475569', flexShrink: 0 }}>{s.published_at}</span>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</span>
                {s.link && (
                  <a href={s.link} target="_blank" rel="noopener noreferrer" style={{ color: '#38bdf8', flexShrink: 0 }}>
                    <ExternalLink size={11} />
                  </a>
                )}
              </div>
            ))}
          </div>
        ))}
        {filtered.length === 0 && <div style={{ padding: '1rem', color: '#334155', fontSize: '0.8rem', textAlign: 'center' }}>해당 언론사 기사 없음</div>}
      </div>
    </div>
  );
}

function YearTab() {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const containerRef = useRef(null);
  const { data, isLoading } = useQuery({ queryKey: ['press-stats-year'], queryFn: fetchPressStatsByYear });

  const years = useMemo(() => data ? Object.keys(data).sort((a, b) => b - a) : [], [data]);
  const currentMap = selectedYear && data ? data[selectedYear] : null;
  const ranked = currentMap ? rankSources(currentMap) : [];
  const max = ranked[0]?.[1] || 1;
  const total = ranked.reduce((s, [, c]) => s + c, 0);

  const yearTotals = useMemo(() => {
    if (!data) return {};
    const t = {};
    for (const y of Object.keys(data)) {
      t[y] = Object.values(data[y]).reduce((s, c) => s + c, 0);
    }
    return t;
  }, [data]);
  const maxYearTotal = Math.max(...Object.values(yearTotals), 1);

  const { data: pressRows = [] } = useQuery({
    queryKey: ['press-stats-press-year', selectedYear],
    queryFn: () => fetchPressStatsByPress({ year: selectedYear }),
    enabled: !!selectedYear && !!selectedSource,
  });

  useEffect(() => {
    if (!selectedSource) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSelectedSource(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedSource]);

  const handleYearClick = (y) => {
    setSelectedYear(y === selectedYear ? null : y);
    setSelectedSource(null);
  };

  return (
    <div ref={containerRef}>
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ minWidth: '200px', flex: '0 0 200px' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>연도 선택</div>
          {isLoading && <div style={{ color: '#475569', fontSize: '0.8rem' }}>로딩 중...</div>}
          {years.map(y => (
            <div key={y} onClick={() => handleYearClick(y)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.6rem', borderRadius: '6px', cursor: 'pointer', marginBottom: '0.25rem', background: selectedYear === y ? '#0f172a' : 'transparent', border: selectedYear === y ? '1px solid #38bdf8' : '1px solid transparent' }}>
              <span style={{ width: '38px', fontSize: '0.82rem', fontWeight: 700, color: selectedYear === y ? '#38bdf8' : '#cbd5e1' }}>{y}</span>
              <div style={{ flex: 1, background: '#1e293b', borderRadius: '2px', height: '10px' }}>
                <div style={{ width: `${(yearTotals[y] / maxYearTotal) * 100}%`, background: selectedYear === y ? '#38bdf8' : '#334155', height: '100%', borderRadius: '2px' }} />
              </div>
              <span style={{ fontSize: '0.72rem', color: '#64748b', width: '28px', textAlign: 'right' }}>{yearTotals[y]}</span>
            </div>
          ))}
        </div>

        <div style={{ flex: 1, minWidth: '280px' }}>
          {selectedYear ? (
            <>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>
                {selectedYear}년 언론사별 보도 건수 <span style={{ color: '#38bdf8' }}>총 {total}건</span>
                {selectedSource && <span style={{ color: '#94a3b8', marginLeft: '0.5rem' }}>— 빈 곳 클릭 시 닫힘</span>}
              </div>
              {ranked.map(([src, cnt], i) => (
                <SourceBar key={src} source={src} count={cnt} max={max} rank={i}
                  selected={selectedSource === src}
                  onClick={() => setSelectedSource(selectedSource === src ? null : src)} />
              ))}
            </>
          ) : (
            <div style={{ color: '#334155', fontSize: '0.85rem', paddingTop: '1.5rem' }}>← 연도를 선택하면 언론사별 건수가 표시됩니다</div>
          )}
        </div>
      </div>
      <SourcePressDrawer source={selectedSource} pressRows={pressRows} onClose={() => setSelectedSource(null)} />
    </div>
  );
}

function MonthTab() {
  const currentYear = String(new Date().getFullYear());
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const containerRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ['press-stats-month', selectedYear],
    queryFn: () => fetchPressStatsByMonth(selectedYear),
  });

  const months = useMemo(() => data ? Object.keys(data).sort((a, b) => b.localeCompare(a)) : [], [data]);
  const monthTotals = useMemo(() => {
    if (!data) return {};
    const t = {};
    for (const m of Object.keys(data)) {
      t[m] = Object.values(data[m]).reduce((s, c) => s + c, 0);
    }
    return t;
  }, [data]);
  const maxMonthTotal = Math.max(...Object.values(monthTotals), 1);

  const currentMap = selectedMonth && data ? data[selectedMonth] : null;
  const ranked = currentMap ? rankSources(currentMap) : [];
  const max = ranked[0]?.[1] || 1;
  const total = ranked.reduce((s, [, c]) => s + c, 0);

  const { data: pressRows = [] } = useQuery({
    queryKey: ['press-stats-press-month', selectedMonth],
    queryFn: () => fetchPressStatsByPress({ month: selectedMonth }),
    enabled: !!selectedMonth && !!selectedSource,
  });

  useEffect(() => {
    if (!selectedSource) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSelectedSource(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedSource]);

  const yearOptions = [];
  for (let y = new Date().getFullYear(); y >= 2010; y--) yearOptions.push(String(y));

  return (
    <div ref={containerRef}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <select value={selectedYear} onChange={e => { setSelectedYear(e.target.value); setSelectedMonth(null); setSelectedSource(null); }}
          style={{ background: '#0f172a', border: '1px solid #1e293b', color: '#e2e8f0', padding: '0.3rem 0.6rem', borderRadius: '6px', fontSize: '0.82rem', cursor: 'pointer' }}>
          {yearOptions.map(y => <option key={y} value={y}>{y}년</option>)}
        </select>
        {isLoading && <span style={{ fontSize: '0.75rem', color: '#475569' }}>로딩 중...</span>}
      </div>
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ minWidth: '200px', flex: '0 0 200px' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>월 선택</div>
          {months.map(m => (
            <div key={m}
              onClick={() => { setSelectedMonth(m === selectedMonth ? null : m); setSelectedSource(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0.6rem', borderRadius: '6px', cursor: 'pointer', marginBottom: '0.2rem', background: selectedMonth === m ? '#0f172a' : 'transparent', border: selectedMonth === m ? '1px solid #38bdf8' : '1px solid transparent' }}>
              <span style={{ width: '46px', fontSize: '0.82rem', fontWeight: 700, color: selectedMonth === m ? '#38bdf8' : '#cbd5e1' }}>{m.slice(5)}월</span>
              <div style={{ flex: 1, background: '#1e293b', borderRadius: '2px', height: '10px' }}>
                <div style={{ width: `${((monthTotals[m] || 0) / maxMonthTotal) * 100}%`, background: selectedMonth === m ? '#38bdf8' : '#334155', height: '100%', borderRadius: '2px' }} />
              </div>
              <span style={{ fontSize: '0.72rem', color: '#64748b', width: '28px', textAlign: 'right' }}>{monthTotals[m] || 0}</span>
            </div>
          ))}
          {!isLoading && months.length === 0 && <div style={{ color: '#334155', fontSize: '0.8rem' }}>데이터 없음</div>}
        </div>
        <div style={{ flex: 1, minWidth: '280px' }}>
          {selectedMonth ? (
            <>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>
                {selectedMonth} 언론사별 보도 건수 <span style={{ color: '#38bdf8' }}>총 {total}건</span>
                {selectedSource && <span style={{ color: '#94a3b8', marginLeft: '0.5rem' }}>— 빈 곳 클릭 시 닫힘</span>}
              </div>
              {ranked.map(([src, cnt], i) => (
                <SourceBar key={src} source={src} count={cnt} max={max} rank={i}
                  selected={selectedSource === src}
                  onClick={() => setSelectedSource(selectedSource === src ? null : src)} />
              ))}
            </>
          ) : (
            <div style={{ color: '#334155', fontSize: '0.85rem', paddingTop: '1.5rem' }}>← 월을 선택하면 언론사별 건수가 표시됩니다</div>
          )}
        </div>
      </div>
      <SourcePressDrawer source={selectedSource} pressRows={pressRows} onClose={() => setSelectedSource(null)} />
    </div>
  );
}

function PressTab() {
  const currentYear = String(new Date().getFullYear());
  const [filterYear, setFilterYear] = useState(currentYear);
  const [filterMonth, setFilterMonth] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const params = useMemo(() => {
    const p = {};
    if (filterYear) p.year = filterYear;
    if (filterMonth) p.month = `${filterYear}-${filterMonth}`;
    return p;
  }, [filterYear, filterMonth]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['press-stats-press', params],
    queryFn: () => fetchPressStatsByPress(params),
  });

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => r.title.toLowerCase().includes(q));
  }, [rows, search]);

  const yearOptions = [];
  for (let y = new Date().getFullYear(); y >= 2010; y--) yearOptions.push(String(y));
  const monthOptions = ['01','02','03','04','05','06','07','08','09','10','11','12'];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <select value={filterYear} onChange={e => { setFilterYear(e.target.value); setFilterMonth(''); }}
          style={{ background: '#0f172a', border: '1px solid #1e293b', color: '#e2e8f0', padding: '0.3rem 0.6rem', borderRadius: '6px', fontSize: '0.82rem', cursor: 'pointer' }}>
          <option value="">전체 연도</option>
          {yearOptions.map(y => <option key={y} value={y}>{y}년</option>)}
        </select>
        <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} disabled={!filterYear}
          style={{ background: '#0f172a', border: '1px solid #1e293b', color: '#e2e8f0', padding: '0.3rem 0.6rem', borderRadius: '6px', fontSize: '0.82rem', cursor: filterYear ? 'pointer' : 'not-allowed', opacity: filterYear ? 1 : 0.4 }}>
          <option value="">전체 월</option>
          {monthOptions.map(m => <option key={m} value={m}>{parseInt(m)}월</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: '#0f172a', border: '1px solid #1e293b', borderRadius: '6px', padding: '0.3rem 0.6rem', flex: 1, maxWidth: '300px' }}>
          <Search size={13} color="#475569" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="보도자료 검색..."
            style={{ background: 'none', border: 'none', outline: 'none', color: '#e2e8f0', fontSize: '0.82rem', width: '100%' }} />
        </div>
        {isLoading && <span style={{ fontSize: '0.75rem', color: '#475569' }}>로딩 중...</span>}
        {!isLoading && <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{filtered.length}건</span>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '0.6rem' }}>
        {filtered.map(row => {
          const isOpen = expanded === row.id;
          const topSources = row.sources.slice(0, 5);
          return (
            <div key={row.id}
              style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px', overflow: 'hidden' }}>
              <div onClick={() => setExpanded(isOpen ? null : row.id)}
                style={{ padding: '0.65rem 0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.2rem' }}>{row.published_at}</div>
                  <div style={{ fontSize: '0.84rem', color: '#e2e8f0', fontWeight: 600, lineHeight: 1.4, marginBottom: '0.3rem' }}>{row.title}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                    {topSources.map((s, i) => (
                      <span key={i} style={{ fontSize: '0.68rem', background: '#1e293b', color: '#94a3b8', padding: '1px 6px', borderRadius: '4px', border: `1px solid ${s.source_type === 'naver' ? '#14532d' : s.source_type === 'bigkinds' ? '#1e3a5f' : '#292524'}` }}>
                        {s.source}
                      </span>
                    ))}
                    {row.count > 5 && <span style={{ fontSize: '0.68rem', color: '#475569' }}>+{row.count - 5}</span>}
                  </div>
                </div>
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem' }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#38bdf8', lineHeight: 1 }}>{row.count}</div>
                  <div style={{ fontSize: '0.62rem', color: '#475569' }}>건</div>
                  {isOpen ? <ChevronUp size={13} color="#475569" /> : <ChevronDown size={13} color="#475569" />}
                </div>
              </div>

              {isOpen && (
                <div style={{ borderTop: '1px solid #1e293b', padding: '0.5rem 0.75rem' }}>
                  {row.sources.map((s, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.25rem 0', borderBottom: i < row.sources.length - 1 ? '1px solid #0f172a' : 'none' }}>
                      <span style={{ fontSize: '0.68rem', padding: '1px 5px', borderRadius: '3px', flexShrink: 0, background: s.source_type === 'naver' ? '#14532d' : s.source_type === 'bigkinds' ? '#1e3a5f' : '#292524', color: s.source_type === 'naver' ? '#4ade80' : s.source_type === 'bigkinds' ? '#7dd3fc' : '#a8a29e' }}>
                        {s.source}
                      </span>
                      <span style={{ fontSize: '0.72rem', color: '#94a3b8', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</span>
                      <span style={{ fontSize: '0.65rem', color: '#475569', flexShrink: 0 }}>{s.published_at}</span>
                      {s.link && (
                        <a href={s.link} target="_blank" rel="noopener noreferrer" style={{ color: '#38bdf8', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                          <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {!isLoading && filtered.length === 0 && (
          <div style={{ color: '#334155', fontSize: '0.85rem', gridColumn: '1/-1', textAlign: 'center', padding: '2rem' }}>
            해당 조건의 보도자료가 없습니다.
          </div>
        )}
      </div>
    </div>
  );
}

export default function MediaStatsPage() {
  const [tab, setTab] = useState(TAB_YEAR);

  const tabs = [
    { key: TAB_YEAR,  label: '연도별', icon: <TrendingUp size={14} /> },
    { key: TAB_MONTH, label: '월별',   icon: <BarChart2 size={14} /> },
    { key: TAB_PRESS, label: '보도자료별', icon: <FileText size={14} /> },
  ];

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '1.5rem 1rem' }}>
      <div style={{ marginBottom: '1.25rem' }}>
        <h1 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#f1f5f9', display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem' }}>
          <BarChart2 size={18} color="#38bdf8" /> 언론사별 보도 현황
        </h1>
        <p style={{ fontSize: '0.78rem', color: '#475569' }}>KNFC 보도자료 매칭 기준 / 광고·홍보 기준 데이터</p>
      </div>

      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.25rem', borderBottom: '1px solid #1e293b', paddingBottom: '0' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.5rem 0.9rem', fontSize: '0.82rem', fontWeight: tab === t.key ? 700 : 400, color: tab === t.key ? '#38bdf8' : '#64748b', background: 'none', border: 'none', borderBottom: tab === t.key ? '2px solid #38bdf8' : '2px solid transparent', cursor: 'pointer', marginBottom: '-1px' }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="card" style={{ background: '#020817', border: '1px solid #1e293b' }}>
        {tab === TAB_YEAR  && <YearTab />}
        {tab === TAB_MONTH && <MonthTab />}
        {tab === TAB_PRESS && <PressTab />}
      </div>
    </div>
  );
}
